/* eslint-disable react/require-default-props */

import React from 'react';
import { Box, Typography } from '@saviynt/design-system';

import './RuleConditions.css';

interface RuleCondition {
  name: string; // Field name (e.g., 'Department', 'Job Title')
  operator: string;
  values: string[]; // Array of values for the condition
}

interface RuleConditionsProps {
  conditions: RuleCondition[]; // Array of conditions to display
  className?: string;
}

/**
 * Component for displaying rule conditions with proper AND logic visualization
 *
 * @param props - The component props
 * @returns JSX element containing the rule conditions display
 */
function RuleConditions({ conditions, className }: RuleConditionsProps) {
  const getDisplayOperator = (operator: string) => {
    const operatorMap = {
      IN: '=', // previously 'is one of'
      EQUALS: '=', // previously 'is'
    };

    return operatorMap[operator as keyof typeof operatorMap] || operator;
  };

  return (
    <Box tag='section' className={`Rules-RuleConditions ${className ?? ''}`}>
      <Box tag='header' className='Rules-RuleConditions-header'>
        <Typography
          kind='h3'
          // color='--color-foreground-neutral-bold'
          className='Rules-RuleConditions-title'>
          Condition
        </Typography>
      </Box>

      {/* Conditions list */}
      <Box className='Rules-RuleConditions-content'>
        {conditions.map((condition, index) => {
          const isLastCondition = index === conditions.length - 1;

          return (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={`${condition.name}-${index}`}
              className='Rules-RuleConditions-row'>
              {/* Condition content */}
              <Box className='Rules-RuleConditions-conditionContent'>
                <Typography
                  kind='body1-bold'
                  color='--color-foreground-neutral-bold'
                  className='Rules-RuleConditions-field'>
                  {condition.name}
                </Typography>

                <Typography
                  kind='body1-bold'
                  color='--color-foreground-info-medium'
                  className='Rules-RuleConditions-operator'>
                  {getDisplayOperator(condition.operator)}
                </Typography>

                {/* Values display */}
                <Box className='Rules-RuleConditions-valueList'>
                  {condition.values.map((value, valueIndex) => (
                    <Box key={value} className='Rules-RuleConditions-valueItem'>
                      <Typography
                        kind='body1-bold'
                        color='--color-foreground-neutral-bold'
                        lineLimit={6}
                        tooltipConfigs={{
                          text: value,
                        }}
                        className='Rules-RuleConditions-value'>
                        &quot;{value}&quot;
                      </Typography>
                      {valueIndex < condition.values.length - 1 && (
                        <Typography
                          kind='body1-bold'
                          className='Rules-RuleConditions-separator'>
                          ,
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* AND connector - only show if not the last condition */}
              {!isLastCondition && (
                <Box className='Rules-RuleConditions-andConnector'>
                  <Typography
                    kind='body1-bold'
                    color='--color-foreground-info-medium'
                    className='Rules-RuleConditions-andText'>
                    And
                  </Typography>
                </Box>
              )}

              {/* Invisible placeholder for last condition to maintain alignment */}
              {isLastCondition && (
                <Box className='Rules-RuleConditions-placeholder' />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default RuleConditions;
